import { filter, mergeMap, take } from 'rxjs/operators';
import { NetworkStatus } from '@cbridge-utils/network';
import { IEpic, isTypeOfAndGetPayload } from '@cbridge-utils/redux-observable';
import { catchApiError } from '@cbridge-packages/api-base';
import { ICoverageApiDependencySegment } from '@cbridge-packages/api-coverage';
import { createApiErrorNotification } from '@cbridge-packages/notification';
import {
  coverageAccountsByReportSelector,
  coverageAccountStoreSelector,
  ICoverageAccountStoreSegment,
} from '@cbridge-packages/coverage-account';
import { CoverageRuleAction } from '../coverageRule.action';
import { CoverageRuleTranslator } from '../coverageRule.translator';

type IDependencies = ICoverageApiDependencySegment;
type IState = ICoverageAccountStoreSegment;

// The rules read resolves the target account by NAME only, so translation needs the report's
// account list. That list loads in parallel with this page (embedded in the reports request) —
// wait until the account store settles before translating, then join names → ids.
export const loadCoverageRuleEpic: IEpic<IState, IDependencies> = (action$, state$, { coverageApi }) => action$.pipe(
  isTypeOfAndGetPayload(CoverageRuleAction.loadList.started),
  mergeMap((params) => state$.pipe(
    filter((state) => {
      const status = coverageAccountStoreSelector(state).listNetworkStatus;
      return status === NetworkStatus.Done || status === NetworkStatus.Failed;
    }),
    take(1),
    mergeMap(() => coverageApi.getCoverageRules(params.ruleSetId).pipe(
      mergeMap((response) => {
        const accounts = coverageAccountsByReportSelector(state$.value, { reportId: params.ruleSetId });
        // result.account is matched by account name AND by externalId: the doc only says
        // "account: string", and the backend has been observed to return the externalId.
        const accountByKey = new Map(accounts.map((account) => [account.name, account]));
        accounts.forEach((account) => {
          if (!accountByKey.has(account.externalId)) {
            accountByKey.set(account.externalId, account);
          }
        });
        const rules = (response.rules ?? []).map((rule) => CoverageRuleTranslator.fromApi(rule, accountByKey));
        return [CoverageRuleAction.loadList.done({ params, result: { ruleSetId: params.ruleSetId, rules } })];
      }),
      catchApiError((error) => [
        CoverageRuleAction.loadList.failed({ params, error }),
        createApiErrorNotification(error),
      ]),
    )),
  )),
);
